import React, { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
	closeOrganizationModal,
	setOrganization,
	updateUserPermissions,
} from "../../js/actions/index";
import { getOrgData, persistOrganizationContext } from "../../helpers/helpers";
import apiHandler from "../../js/apiHandler";

function getParent(orgs, currOrg) {
	const tree =
		orgs[0]?.organization_name === "_global_"
			? orgs[0].child_organizations
			: orgs;
	let parent = {};

	const filterOrg = (x) => {
		if (!Array.isArray(x)) return;
		for (let i = 0; i < x.length; i++) {
			if (x[i].organization_name === currOrg) {
				parent = x[i];
				break;
			} else if (x[i].child_organizations?.length) {
				for (let y = 0; y < x[i].child_organizations.length; y++) {
					if (x[i].child_organizations[y].organization_name === currOrg) {
						parent = x[i];
						break;
					}
				}
				if (!Object.keys(parent).length) {
					filterOrg(x[i].child_organizations);
				}
			}
		}
	};

	filterOrg(tree);
	return parent;
}

function OrgTreeNodes({ list, currentOrg, onSwitch }) {
	return list.map((item, i) => {
		const hasChildren = item.child_organizations?.length > 0;
		const orgName = item.organization_name;
		const orgTitle = item.title || orgName;
		const isActive = currentOrg === orgName;

		return (
			<div key={i}>
				<li
					onClick={!isActive ? () => onSwitch(item) : null}
					className={isActive ? "highlight" : ""}>
					<span>{orgTitle}</span>
				</li>
				<ul className="child">
					{hasChildren && (
						<OrgTreeNodes
							list={item.child_organizations}
							currentOrg={currentOrg}
							onSwitch={onSwitch}
						/>
					)}
				</ul>
			</div>
		);
	});
}

const OrganizationTree = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const org = useSelector((state) => state.roleManager.org);
	const user = useSelector((state) => state.authState.user);

	const resolveParent = useCallback(
		(currentOrg) => {
			const tree = user?.organization_role_tree;
			if (!tree) return {};

			let parent = getParent(tree, currentOrg);

			if (
				parent?.organization_name === "propertyowners" ||
				parent?.organization_name === "propertymanagers"
			) {
				const orgData = getOrgData(user, currentOrg);
				parent = orgData?.currOrg ?? {};
			}

			return parent ?? {};
		},
		[user],
	);

	const [parentOrg, setParentOrg] = useState(() => resolveParent(org));

	useEffect(() => {
		setParentOrg(resolveParent(org));
	}, [org, resolveParent]);

	const switchOrg = useCallback(
		async (orgData) => {
			const orgName = orgData.organization_name;
			const orgTitle = orgData.title || orgName;
			const orgId = orgData.organization_id;

			dispatch(closeOrganizationModal(""));
			setParentOrg(orgData);

			navigate("/dashboard");
			dispatch(setOrganization({ org: orgName, orgTitle, orgId }));
			persistOrganizationContext({ org: orgName, orgTitle, orgId });
			window.dispatchEvent(new CustomEvent("organization-switched"));

			try {
				const resp = await apiHandler.get({
					apiName: "accapi",
					path: `/currentuser/${orgName}/permissions`,
				});
				if (Array.isArray(resp?.data?.permissions)) {
					dispatch(updateUserPermissions(resp.data.permissions));
				}
			} catch (err) {
				console.error("Failed to load permissions for org", orgName, err);
			}
		},
		[dispatch, navigate],
	);

	if (!parentOrg || !Object.keys(parentOrg).length) return null;

	return (
		<ul className="org-tree">
			<OrgTreeNodes list={[parentOrg]} currentOrg={org} onSwitch={switchOrg} />
		</ul>
	);
};

export default OrganizationTree;
